import { Component, Input, OnInit, TemplateRef, ViewChild, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from 'src/app/store.service';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { FacebookPixel } from '../../preferences.component';

export interface CountryOption {
  name: string;
  code: string; // ISO 3166-1 alpha-2 lowercase (e.g. 'my')
  phoneCode: string; // e.g. '+60'
  flag?: string;
}

export const COUNTRIES_LIST: CountryOption[] = [
  { name: 'Malaysia', code: 'my', phoneCode: '+60' },
  { name: 'Singapore', code: 'sg', phoneCode: '+65' },
  { name: 'Indonesia', code: 'id', phoneCode: '+62' },
  { name: 'Thailand', code: 'th', phoneCode: '+66' },
  { name: 'Philippines', code: 'ph', phoneCode: '+63' },
  { name: 'Vietnam', code: 'vn', phoneCode: '+84' },
  { name: 'Brunei', code: 'bn', phoneCode: '+673' },
  { name: 'United States', code: 'us', phoneCode: '+1' },
  { name: 'United Kingdom', code: 'gb', phoneCode: '+44' },
  { name: 'Australia', code: 'au', phoneCode: '+61' },
  { name: 'China', code: 'cn', phoneCode: '+86' },
  { name: 'Hong Kong', code: 'hk', phoneCode: '+852' },
  { name: 'Taiwan', code: 'tw', phoneCode: '+886' },
  { name: 'Japan', code: 'jp', phoneCode: '+81' },
  { name: 'South Korea', code: 'kr', phoneCode: '+82' },
  { name: 'India', code: 'in', phoneCode: '+91' },
  { name: 'Canada', code: 'ca', phoneCode: '+1' },
  { name: 'New Zealand', code: 'nz', phoneCode: '+64' },
  { name: 'United Arab Emirates', code: 'ae', phoneCode: '+971' },
  { name: 'Saudi Arabia', code: 'sa', phoneCode: '+966' },
  { name: 'Germany', code: 'de', phoneCode: '+49' },
  { name: 'France', code: 'fr', phoneCode: '+33' },
  { name: 'Italy', code: 'it', phoneCode: '+39' },
  { name: 'Spain', code: 'es', phoneCode: '+34' },
  { name: 'Netherlands', code: 'nl', phoneCode: '+31' },
  { name: 'Switzerland', code: 'ch', phoneCode: '+41' },
  { name: 'Sweden', code: 'se', phoneCode: '+46' },
  { name: 'Norway', code: 'no', phoneCode: '+47' },
  { name: 'Denmark', code: 'dk', phoneCode: '+45' },
  { name: 'Brazil', code: 'br', phoneCode: '+55' },
  { name: 'Mexico', code: 'mx', phoneCode: '+52' },
  { name: 'South Africa', code: 'za', phoneCode: '+27' },
  { name: 'Turkey', code: 'tr', phoneCode: '+90' },
  { name: 'Egypt', code: 'eg', phoneCode: '+20' },
  { name: 'Pakistan', code: 'pk', phoneCode: '+92' },
  { name: 'Bangladesh', code: 'bd', phoneCode: '+880' },
  { name: 'Sri Lanka', code: 'lk', phoneCode: '+94' },
  { name: 'Cambodia', code: 'kh', phoneCode: '+855' },
  { name: 'Myanmar', code: 'mm', phoneCode: '+95' },
  { name: 'Laos', code: 'la', phoneCode: '+856' },
  { name: 'Macau', code: 'mo', phoneCode: '+853' },
  { name: 'Ireland', code: 'ie', phoneCode: '+353' },
  { name: 'Belgium', code: 'be', phoneCode: '+32' },
  { name: 'Austria', code: 'at', phoneCode: '+43' },
  { name: 'Poland', code: 'pl', phoneCode: '+48' },
  { name: 'Portugal', code: 'pt', phoneCode: '+351' },
  { name: 'Greece', code: 'gr', phoneCode: '+30' },
  { name: 'Qatar', code: 'qa', phoneCode: '+974' },
  { name: 'Kuwait', code: 'kw', phoneCode: '+965' },
];

export interface ContentLineItem {
  id: string;
  quantity?: number;
  item_price?: number;
}

@Component({
  selector: 'app-facebook-pixel-manual-event',
  templateUrl: './facebook-pixel-manual-event.component.html',
  styleUrls: ['./facebook-pixel-manual-event.component.scss'],
})
export class FacebookPixelManualEventComponent implements OnInit {
  @Input() facebookPixels: FacebookPixel[] = [];
  @Input() storeId: any = '';
  @Input() bearer: string = '';

  @ViewChild('manualEventModal') manualEventModal!: TemplateRef<any>;
  @ViewChild('liveConfirmModal') liveConfirmModal!: TemplateRef<any>;

  modalRef: NgbModalRef | null = null;
  confirmModalRef: NgbModalRef | null = null;
  form!: FormGroup;

  mode: 'test' | 'live' = 'test';
  isSubmitting = false;
  submissionResult: {
    status: 'success' | 'error';
    message: string;
    traceId?: string;
    timestamp?: string;
    mode?: 'test' | 'live';
  } | null = null;

  dateError: string = '';

  // Reference ID generated on submit when the user left Order / Reference No. empty
  autoGeneratedOrderId: string = '';

  // Products Section Collapse state
  isProductsCollapsed: boolean = true;

  // Interaction / Touched flags to prevent validation errors on a fresh form
  hasAttemptedSubmit: boolean = false;
  emailBlurred: boolean = false;
  phoneBlurred: boolean = false;
  consentBlurred: boolean = false;
  amountBlurred: boolean = false;
  actionSourceTouched: boolean = false;

  // Country & Dropdown State
  countriesList = COUNTRIES_LIST;
  countrySearchQuery: string = '';
  isCountryDropdownOpen: boolean = false;

  // Phone Country Code Dropdown State
  phoneSearchQuery: string = '';
  isPhoneDropdownOpen: boolean = false;

  // Storefront Orders Picker State
  storefrontOrders: any[] = [];
  isLoadingOrders: boolean = false;
  ordersLoadError: string = '';
  orderSearchQuery: string = '';
  isOrderDropdownOpen: boolean = false;
  selectedStorefrontOrder: any = null;
  showStorefrontOrderError: boolean = false;

  // 4 Sale Location Cards
  actionSources = [
    {
      value: 'physical_store',
      title: 'Physical Store',
      subtitle: 'Walk-in & offline retail sales',
      icon: 'fi-rr-shop',
      maxDays: 62,
    },
    {
      value: 'phone_call',
      title: 'Phone Call',
      subtitle: 'Orders taken over a phone call',
      icon: 'fi-rr-phone-call',
      maxDays: 7,
    },
    {
      value: 'chat',
      title: 'Chat / WhatsApp / DM',
      subtitle: 'Orders via WhatsApp, Messenger, Instagram DM or other chat',
      icon: 'fi-rr-comments',
      maxDays: 7,
    },
    {
      value: 'website',
      title: 'Storefront',
      subtitle: 'Online orders from your storefront',
      icon: 'fi-rr-browser',
      maxDays: 7,
    },
  ];

  // Most commonly used Meta events
  eventsList = [
    { value: 'Purchase', label: 'Purchase — Sale / Order completed' },
    { value: 'Lead', label: 'Lead — Inquiry / Contact info submitted' },
    { value: 'AddToCart', label: 'AddToCart — Item added to cart' },
    { value: 'InitiateCheckout', label: 'InitiateCheckout — Checkout started' },
    { value: 'AddPaymentInfo', label: 'AddPaymentInfo — Payment info entered' },
    { value: 'ViewContent', label: 'ViewContent — Page or product viewed' },
    { value: 'Contact', label: 'Contact — Customer initiated message' },
    { value: 'CompleteRegistration', label: 'CompleteRegistration — User signed up' },
    { value: 'Subscribe', label: 'Subscribe — Paid subscription started' },
    { value: 'CUSTOM', label: 'Custom Event — Enter custom name...' },
  ];

  websiteEventsList = [
    { value: 'Purchase', label: 'Purchase — Sale / Order completed' },
    { value: 'InitiateCheckout', label: 'InitiateCheckout — Checkout started' },
    { value: 'AddToCart', label: 'AddToCart — Item added to cart' },
    { value: 'ViewContent', label: 'ViewContent — Page or product viewed' },
    { value: 'CUSTOM', label: 'Custom Event — Enter custom name...' },
  ];

  currencies: string[] = ['MYR', 'USD', 'SGD', 'IDR', 'THB', 'PHP', 'EUR', 'GBP', 'AUD', 'TWD', 'HKD', 'JPY'];

  minEventDateTime = '';
  maxEventDateTime = '';

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private storeService: StoreService,
    private utils: UtilitiesService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  get hasNonCapiPixel(): boolean {
    if (!this.allPixels || this.allPixels.length === 0) return false;
    return this.allPixels.some((p) => !this.isCapiPixel(p));
  }

  get availableEventsList(): any[] {
    return this.form?.get('action_source')?.value === 'website' ? this.websiteEventsList : this.eventsList;
  }

  trackByEventValue(index: number, item: any): string {
    return item?.value || String(index);
  }

  get isLiveStorefront(): boolean {
    return this.mode === 'live' && this.form?.get('action_source')?.value === 'website';
  }

  get isLiveStorefrontAwaitingOrder(): boolean {
    return false;
  }

  get isOrderLocked(): boolean {
    return false;
  }

  get filteredStorefrontOrders(): any[] {
    const q = (this.orderSearchQuery || '').trim().toLowerCase();
    if (!q) return this.storefrontOrders;
    return this.storefrontOrders.filter((o) => {
      const storeOrderNo = String(o.store_order_id || o.order_id || o.order_no || '').toLowerCase();
      const fiv5sOrderNo = String(o.fiv5s_order_id || '').toLowerCase();
      const name = String(o.customer_name || '').toLowerCase();
      const email = String(o.customer_email || '').toLowerCase();
      return storeOrderNo.includes(q) || fiv5sOrderNo.includes(q) || name.includes(q) || email.includes(q);
    });
  }

  getFlagUrl(code: string): string {
    const c = (code || 'my').toLowerCase().trim();
    return `https://flagcdn.com/16x12/${c}.png`;
  }

  formatDateForInput(date: Date): string {
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formatOrderDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = d.getDate();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      let hours = d.getHours();
      const minutes = d.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const minStr = minutes < 10 ? '0' + minutes : minutes;
      return `${day} ${month} ${year}, ${hours}:${minStr} ${ampm}`;
    } catch (e) {
      return dateStr;
    }
  }

  calcDateBounds(actionSource = 'physical_store') {
    const now = new Date();
    const sourceObj = this.actionSources.find((s) => s.value === actionSource);
    const daysAllowed = sourceObj ? sourceObj.maxDays : 62;
    const pastDate = new Date(now.getTime() - daysAllowed * 24 * 60 * 60 * 1000);

    this.maxEventDateTime = this.formatDateForInput(now);
    this.minEventDateTime = this.formatDateForInput(pastDate);
  }

  // Timezone Display
  getTimezoneDisplay(): string {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kuala_Lumpur';
      const now = new Date();
      const offsetMinutes = -now.getTimezoneOffset();
      const sign = offsetMinutes >= 0 ? '+' : '-';
      const hours = Math.floor(Math.abs(offsetMinutes) / 60);
      const mins = Math.abs(offsetMinutes) % 60;
      const gmtStr = `GMT${sign}${hours}${mins > 0 ? `:${mins < 10 ? '0' : ''}${mins}` : ''}`;

      let region = tz.replace(/_/g, ' ').split('/').pop() || '';
      if (tz.includes('Kuala_Lumpur') || tz.includes('Singapore') || tz.includes('Malaysia')) {
        region = 'Malaysia time';
      } else if (region) {
        region = `${region} time`;
      } else {
        region = 'Local time';
      }
      return `${region} (${gmtStr})`;
    } catch (e) {
      return 'Malaysia time (GMT+8)';
    }
  }

  // Unambiguous Date & Time formatting: "1 Oct 2026, 12:26 PM"
  getFormattedEventDateTime(): string {
    const val = this.form?.get('event_time')?.value;
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';

    const day = d.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minStr = minutes < 10 ? '0' + minutes : minutes;

    return `${day} ${month} ${year}, ${hours}:${minStr} ${ampm}`;
  }

  // Only pixels with Conversions API (CAPI) configured
  get capiPixels(): FacebookPixel[] {
    if (!this.facebookPixels || !Array.isArray(this.facebookPixels)) return [];
    return this.facebookPixels.filter(
      (p) => p && p.pixel_id && (p.capi === 'account' || p.capi === 'manual')
    );
  }

  get allPixels(): FacebookPixel[] {
    return this.facebookPixels && Array.isArray(this.facebookPixels) ? this.facebookPixels : [];
  }

  isCapiPixel(p: FacebookPixel): boolean {
    return !!p && (p.capi === 'account' || p.capi === 'manual');
  }

  getCountryNameByCode(code: string): string {
    if (!code || code.toUpperCase() === 'GLOBAL') return 'Global';
    const match = this.countriesList.find((c) => c.code.toLowerCase() === code.toLowerCase());
    return match ? match.name : code.toUpperCase();
  }

  getPixelDisplayLabel(p: FacebookPixel): string {
    if (!p) return '';
    const hasCapi = this.isCapiPixel(p);
    if (!hasCapi) {
      return `${p.pixel_id} · No CAPI token`;
    }
    const countryStr = p.countries && p.countries.length > 0
      ? p.countries.map((c) => this.getCountryNameByCode(c)).join(', ')
      : 'Global';
    return `${p.pixel_id} · ${countryStr} · CAPI`;
  }

  initForm() {
    const defaultPixel = this.capiPixels && this.capiPixels.length > 0 ? this.capiPixels[0].pixel_id : '';
    const nowStr = this.formatDateForInput(new Date());

    this.calcDateBounds('website');
    this.countrySearchQuery = '';
    this.isCountryDropdownOpen = false;
    this.phoneSearchQuery = '';
    this.isPhoneDropdownOpen = false;
    this.isProductsCollapsed = true;
    this.selectedStorefrontOrder = null;
    this.showStorefrontOrderError = false;
    this.isOrderDropdownOpen = false;
    this.orderSearchQuery = '';
    this.resetValidationState();

    this.form = this.fb.group({
      pixel_id: [defaultPixel, Validators.required],
      test_event_code: [''],
      action_source: ['website', Validators.required],
      event_time: [nowStr, Validators.required],
      event_select: ['Purchase', Validators.required],
      custom_event_name: [''],
      value: [''],
      currency: ['MYR'],
      order_id: [''],

      // Customer Details (always expanded)
      customer_email: ['', [Validators.email]],
      customer_phone_prefix: ['+60'],
      customer_phone_number: [''],
      first_name: [''],
      last_name: [''],
      city: [''],
      postcode: [''],
      country: [''], // ISO 3166-1 alpha-2 lowercase, empty = not specified
      customer_id: [''],
      customer_consent: [false],

      // Products FormArray (Line items)
      contents: this.fb.array([]),
    });

    // Listen to action_source changes to re-calculate allowed date bounds
    this.form.get('action_source')?.valueChanges.subscribe((newSource) => {
      if (newSource) {
        this.calcDateBounds(newSource);
      }
      this.validateEventDate();
    });

    // Listen to pixel_id changes
    this.form.get('pixel_id')?.valueChanges.subscribe(() => {
      if (this.form.get('action_source')?.value === 'website') {
        this.loadStorefrontOrders();
      }
    });

    // Listen to date changes
    this.form.get('event_time')?.valueChanges.subscribe(() => {
      this.validateEventDate();
    });
  }

  resetValidationState() {
    this.hasAttemptedSubmit = false;
    this.emailBlurred = false;
    this.phoneBlurred = false;
    this.consentBlurred = false;
    this.amountBlurred = false;
    this.actionSourceTouched = false;
  }

  // --- Blur Handlers for Inline Validation on interaction ---
  onEmailBlur() {
    this.emailBlurred = true;
  }

  onPhoneBlur() {
    this.phoneBlurred = true;
  }

  onConsentBlur() {
    this.consentBlurred = true;
  }

  onAmountBlur() {
    this.amountBlurred = true;
  }

  // Meta requires value + currency for Purchase events in both Test and Live mode
  get isConversionAmountRequired(): boolean {
    return this.form?.get('event_select')?.value === 'Purchase';
  }

  get showAmountError(): boolean {
    if (!this.isConversionAmountRequired) return false;
    const val = this.form?.get('value')?.value;
    const isInvalid = val === '' || val === null || isNaN(Number(val)) || Number(val) <= 0;
    return isInvalid && (this.hasAttemptedSubmit || this.amountBlurred);
  }

  // Email or phone is required in both Test and Live mode
  get showContactError(): boolean {
    if (this.hasCustomerIdentifier) return false;
    return this.hasAttemptedSubmit || this.emailBlurred || this.phoneBlurred;
  }

  get hasInvalidEmail(): boolean {
    const email = (this.form?.get('customer_email')?.value || '').trim();
    return !!email && !!this.form?.get('customer_email')?.invalid;
  }

  get showEmailFormatError(): boolean {
    return this.hasInvalidEmail && (this.hasAttemptedSubmit || this.emailBlurred);
  }

  get showConsentError(): boolean {
    if (this.mode !== 'live') return false;
    if (this.form?.get('customer_consent')?.value) return false;
    return this.hasAttemptedSubmit || this.consentBlurred;
  }

  get showActionSourceError(): boolean {
    if (this.mode !== 'live') return false;
    if (this.form?.get('action_source')?.value) return false;
    return this.hasAttemptedSubmit || this.actionSourceTouched;
  }

  // --- Click Outside HostListener to close all custom dropdowns ---
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target) return;

    // Check Order Dropdown
    if (this.isOrderDropdownOpen) {
      const orderWrapper = document.querySelector('.order-picker-wrapper');
      if (orderWrapper && !orderWrapper.contains(target)) {
        this.closeOrderDropdown();
      }
    }

    // Check Phone Dropdown
    if (this.isPhoneDropdownOpen) {
      const phoneWrapper = document.querySelector('.custom-phone-select-wrapper');
      if (phoneWrapper && !phoneWrapper.contains(target)) {
        this.closePhoneDropdown();
      }
    }

    // Check Country Dropdown
    if (this.isCountryDropdownOpen) {
      const countryWrapper = document.querySelector('.custom-country-select-wrapper');
      if (countryWrapper && !countryWrapper.contains(target)) {
        this.closeCountryDropdown();
      }
    }
  }

  // --- Country Dropdown Helpers ---
  get filteredCountries(): CountryOption[] {
    const q = (this.countrySearchQuery || '').toLowerCase().trim();
    if (!q) return this.countriesList;
    return this.countriesList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }

  getSelectedCountry(): CountryOption | null {
    const currentCode = (this.form?.get('country')?.value || '').toLowerCase().trim();
    if (!currentCode) return null;
    return this.countriesList.find((c) => c.code === currentCode) || null;
  }

  clearCountry(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.form.patchValue({ country: '' });
    this.closeCountryDropdown();
  }

  toggleCountryDropdown() {
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
    if (this.isCountryDropdownOpen) {
      this.countrySearchQuery = '';
      this.isPhoneDropdownOpen = false;
      this.isOrderDropdownOpen = false;
    }
  }

  closeCountryDropdown() {
    this.isCountryDropdownOpen = false;
    this.countrySearchQuery = '';
  }

  selectCountry(country: CountryOption, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.form.patchValue({ country: country.code });
    this.closeCountryDropdown();
  }

  // --- Phone Dropdown Helpers ---
  get filteredPhoneCountries(): CountryOption[] {
    const q = (this.phoneSearchQuery || '').toLowerCase().trim();
    if (!q) return this.countriesList;
    return this.countriesList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.phoneCode.toLowerCase().includes(q)
    );
  }

  getSelectedPhoneCountry(): CountryOption {
    const currentPrefix = (this.form?.get('customer_phone_prefix')?.value || '+60').trim();
    return this.countriesList.find((c) => c.phoneCode === currentPrefix) || this.countriesList[0];
  }

  togglePhoneDropdown() {
    this.isPhoneDropdownOpen = !this.isPhoneDropdownOpen;
    if (this.isPhoneDropdownOpen) {
      this.phoneSearchQuery = '';
      this.isCountryDropdownOpen = false;
      this.isOrderDropdownOpen = false;
    }
  }

  closePhoneDropdown() {
    this.isPhoneDropdownOpen = false;
    this.phoneSearchQuery = '';
  }

  selectPhoneCountry(country: CountryOption, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.form.patchValue({ customer_phone_prefix: country.phoneCode });
    this.closePhoneDropdown();
  }

  // --- Consent Checkbox Helpers ---
  toggleConsent(event?: Event) {
    if (event) {
      const target = event.target as HTMLElement;
      if (target && target.tagName === 'INPUT') {
        return;
      }
    }
    const currentVal = !!this.form.get('customer_consent')?.value;
    this.form.patchValue({ customer_consent: !currentVal });
  }

  onConsentChange() {
    // Immediate auto removal of error handled by getter showConsentError
  }

  selectActionSource(sourceValue: string) {
    this.actionSourceTouched = true;
    this.form.patchValue({ action_source: sourceValue });
    if (sourceValue === 'website') {
      this.loadStorefrontOrders();
    } else {
      if (this.selectedStorefrontOrder) {
        this.clearSelectedOrder();
      }
    }
  }

  loadStorefrontOrders() {
    const pixelId = (this.form.get('pixel_id')?.value || '').trim();
    const token = this.bearer || localStorage.getItem('bearer') || '';
    const storeId = this.storeId || localStorage.getItem('store_id');
    if (!pixelId || !storeId) return;

    this.isLoadingOrders = true;
    this.ordersLoadError = '';
    this.storeService.getStorefrontOrders(token, { store_id: storeId, pixel_id: pixelId, days: 7 }).subscribe({
      next: (res: any) => {
        this.isLoadingOrders = false;
        this.storefrontOrders = (res?.data || []).map((o: any) => this.withTrackingDisplay(o));
        if (this.selectedStorefrontOrder) {
          const updated = this.storefrontOrders.find((o) => o.order_id === this.selectedStorefrontOrder.order_id);
          if (updated) {
            this.selectedStorefrontOrder = updated;
            if (this.mode === 'live' && !updated.is_selectable_live) {
              this.clearSelectedOrder();
            }
          }
        }
      },
      error: (err: any) => {
        this.isLoadingOrders = false;
        this.ordersLoadError = err?.error?.message || err?.message || 'Failed to load storefront orders.';
      },
    });
  }

  // The API returns tracking_status + sent_at; build the badge text and the
  // hover reason here so a locked order says why it cannot be picked.
  private withTrackingDisplay(order: any) {
    const labels: Record<string, string> = {
      sent: 'Sent to Meta',
      manually_pushed: 'Sent manually',
      failed: 'Payment failed',
      not_sent: 'Not sent',
    };
    const label = labels[order?.tracking_status] || labels['not_sent'];
    const alreadySent = order?.tracking_status === 'sent' || order?.tracking_status === 'manually_pushed';
    const tooltip = alreadySent
      ? `${this.utils.translate('Already sent to Meta on')} ${order.sent_at || ''}. ${this.utils.translate('Sending it again would count the sale twice.')}`.trim()
      : '';
    return {
      ...order,
      tracking_label: this.utils.translate(label),
      tooltip: tooltip || order?.unselectable_reason || '',
    };
  }

  toggleOrderDropdown() {
    this.isOrderDropdownOpen = !this.isOrderDropdownOpen;
    if (this.isOrderDropdownOpen) {
      this.orderSearchQuery = '';
      this.isPhoneDropdownOpen = false;
      this.isCountryDropdownOpen = false;
      if (this.storefrontOrders.length === 0) {
        this.loadStorefrontOrders();
      }
    }
  }

  closeOrderDropdown() {
    this.isOrderDropdownOpen = false;
    this.orderSearchQuery = '';
  }

  selectStorefrontOrder(order: any) {
    if (this.mode === 'live' && !order.is_selectable_live) {
      return;
    }
    this.selectedStorefrontOrder = order;
    this.showStorefrontOrderError = false;
    this.isOrderDropdownOpen = false;

    // Prefill form from order
    const formattedDate = order.created_at ? this.formatDateForInput(new Date(order.created_at)) : this.formatDateForInput(new Date());
    this.form.patchValue({
      event_time: formattedDate,
      currency: order.currency || 'MYR',
      value: order.total_amount ? Number(order.total_amount).toFixed(2) : '0.00',
      order_id: String(order.store_order_id || order.order_id),
      customer_email: order.customer_email || '',
      customer_phone_prefix: order.customer_phone_prefix || '+60',
      customer_phone_number: order.customer_phone_number || '',
      first_name: order.first_name || '',
      last_name: order.last_name || '',
      city: order.city || '',
      postcode: order.postcode || '',
      country: order.country || '',
      customer_id: order.customer_id || '',
      event_select: 'Purchase',
    });

    // Populate line items
    while (this.contents.length > 0) {
      this.contents.removeAt(0);
    }
    if (order.line_items && order.line_items.length > 0) {
      order.line_items.forEach((item: any) => {
        this.addContentItem(item.id, item.name, item.quantity, item.item_price);
      });
      this.isProductsCollapsed = false;
    } else {
      this.isProductsCollapsed = true;
    }

    this.validateEventDate();
  }

  clearSelectedOrder() {
    this.selectedStorefrontOrder = null;
    this.showStorefrontOrderError = false;
    // Clear prefilled fields
    this.setEventTimeToNow();
    this.form.patchValue({
      value: '',
      currency: 'MYR',
      order_id: '',
      customer_email: '',
      customer_phone_prefix: '+60',
      customer_phone_number: '',
      first_name: '',
      last_name: '',
      city: '',
      postcode: '',
      country: '',
      customer_id: '',
      event_select: 'Purchase',
    });
    while (this.contents.length > 0) {
      this.contents.removeAt(0);
    }
    this.isProductsCollapsed = true;
  }

  // forceReset clears all fields even when staying in the same mode (e.g. when reopening the modal)
  setMode(newMode: 'test' | 'live', forceReset = false) {
    const isModeSwitch = this.mode !== newMode;
    this.mode = newMode;
    this.submissionResult = null;
    this.dateError = '';
    this.resetValidationState();

    if (isModeSwitch || forceReset) {
      this.autoGeneratedOrderId = '';
      this.selectedStorefrontOrder = null;
      this.showStorefrontOrderError = false;
      this.isProductsCollapsed = true;

      // Clear line items
      while (this.contents.length > 0) {
        this.contents.removeAt(0);
      }

      const nowStr = this.formatDateForInput(new Date());

      // Reset all user-fillable fields so data does not carry over between tabs
      this.form.patchValue({
        test_event_code: '',
        event_time: nowStr,
        event_select: 'Purchase',
        custom_event_name: '',
        value: '',
        currency: 'MYR',
        order_id: '',
        customer_email: '',
        customer_phone_prefix: '+60',
        customer_phone_number: '',
        first_name: '',
        last_name: '',
        city: '',
        postcode: '',
        country: '',
        customer_id: '',
        customer_consent: false,
      });

      if (!this.form.get('action_source')?.value) {
        this.form.patchValue({ action_source: 'website' });
      }
    }

    if (newMode === 'test') {
      this.form.get('test_event_code')?.setValidators([Validators.required]);
      if (!this.form.get('action_source')?.value) {
        this.form.patchValue({ action_source: 'website' });
      }
    } else {
      this.form.get('test_event_code')?.clearValidators();
    }
    this.form.get('test_event_code')?.updateValueAndValidity();
  }

  setEventTimeToNow() {
    const nowStr = this.formatDateForInput(new Date());
    this.form.patchValue({ event_time: nowStr });
    this.dateError = '';
  }

  validateEventDate() {
    const currentVal = this.form.get('event_time')?.value;
    if (!currentVal) {
      this.dateError = this.utils.translate('Please enter an event date & time.');
      return false;
    }

    const parsed = new Date(currentVal);
    const now = new Date();
    const actionSource = this.form.get('action_source')?.value || 'physical_store';
    const sourceObj = this.actionSources.find((s) => s.value === actionSource);
    const maxDays = sourceObj ? sourceObj.maxDays : 62;
    const pastMin = new Date(now.getTime() - maxDays * 24 * 60 * 60 * 1000);

    if (parsed > now) {
      this.dateError = this.utils.translate('Event date cannot be in the future.');
      return false;
    } else if (parsed < pastMin) {
      this.dateError = this.utils.translate(
        `Event date is older than the allowed ${maxDays}-day window for "${sourceObj?.title}".`
      );
      return false;
    } else {
      this.dateError = '';
      return true;
    }
  }

  get currentActionSourceObj() {
    const selectedSource = this.form?.get('action_source')?.value;
    return this.actionSources.find((s) => s.value === selectedSource) || this.actionSources[0];
  }

  get currentActionSourceMaxDays(): number {
    return this.currentActionSourceObj?.maxDays || 62;
  }

  // --- Customer Match Strength Indicator ---
  get hasCustomerEmail(): boolean {
    const email = (this.form.get('customer_email')?.value || '').trim();
    return !!email && this.form.get('customer_email')?.valid;
  }

  get hasCustomerPhone(): boolean {
    const rawNumber = (this.form.get('customer_phone_number')?.value || '').trim();
    const digits = rawNumber.replace(/\D/g, '');
    return digits.length >= 5;
  }

  getCombinedPhone(): string {
    const rawNumber = (this.form.get('customer_phone_number')?.value || '').trim();
    let numberDigits = rawNumber.replace(/\D/g, '');
    if (!numberDigits) return '';
    // Number typed/pasted in full international format (e.g. +60123456789) — don't prepend the prefix again
    if (rawNumber.startsWith('+')) return numberDigits;
    const prefixDigits = (this.form.get('customer_phone_prefix')?.value || '+60').replace(/\D/g, '');
    // Strip leading zeros from local number if prefix is provided
    if (prefixDigits && numberDigits.startsWith('0')) {
      numberDigits = numberDigits.replace(/^0+/, '');
    }
    const combined = (prefixDigits + numberDigits).replace(/^0+/, '');
    return combined;
  }

  get hasCustomerName(): boolean {
    const fn = (this.form.get('first_name')?.value || '').trim();
    const ln = (this.form.get('last_name')?.value || '').trim();
    return !!(fn || ln);
  }

  get hasCustomerLocation(): boolean {
    const city = (this.form.get('city')?.value || '').trim();
    const postcode = (this.form.get('postcode')?.value || '').trim();
    const country = (this.form.get('country')?.value || '').trim();
    return !!(city || postcode || country);
  }

  get matchStrength(): 'none' | 'weak' | 'good' | 'strong' {
    const email = this.hasCustomerEmail;
    const phone = this.hasCustomerPhone;
    const name = this.hasCustomerName;
    const location = this.hasCustomerLocation;

    if (!email && !phone) {
      return 'none';
    }
    if (email && phone && name && location) {
      return 'strong';
    }
    if (email && phone) {
      return 'good';
    }
    return 'weak';
  }

  get hasCustomerIdentifier(): boolean {
    return this.hasCustomerEmail || this.hasCustomerPhone;
  }

  // --- Products Section & Line Items ---
  get contents(): FormArray {
    return this.form.get('contents') as FormArray;
  }

  toggleProductsCollapse() {
    this.isProductsCollapsed = !this.isProductsCollapsed;
    if (!this.isProductsCollapsed && this.contents.length === 0) {
      this.addContentItem();
    }
  }

  get productsSummaryText(): string {
    const validRows = this.contents.controls.filter(
      (c) => (c.get('id')?.value || '').trim().length > 0
    );
    if (validRows.length === 0) {
      return 'No products added';
    }
    const count = validRows.reduce(
      (sum, c) => sum + (Number(c.get('quantity')?.value) || 1),
      0
    );
    const total = validRows.reduce((sum, c) => {
      const qty = Number(c.get('quantity')?.value) || 1;
      const price = Number(c.get('item_price')?.value) || 0;
      return sum + qty * price;
    }, 0);
    const currency = this.form?.get('currency')?.value || 'MYR';
    const itemText = count === 1 ? 'item' : 'items';
    return `${count} ${itemText} · ${currency} ${total.toFixed(2)}`;
  }

  addContentItem(id = '', name = '', quantity = 1, itemPrice: number | string = '') {
    const formattedPrice = itemPrice !== '' && itemPrice !== null && !isNaN(Number(itemPrice))
      ? Number(itemPrice).toFixed(2)
      : (itemPrice ?? '');

    this.contents.push(
      this.fb.group({
        id: [id],
        name: [name],
        quantity: [quantity || 1, [Validators.min(1)]],
        item_price: [formattedPrice, [Validators.min(0)]],
        skuBlurred: [false],
      })
    );
  }

  removeContentItem(index: number) {
    this.contents.removeAt(index);
  }

  onSkuBlur(ctrl: any) {
    ctrl.patchValue({ skuBlurred: true });
  }

  isRowTouched(ctrl: any): boolean {
    const name = (ctrl.get('name')?.value || '').trim();
    const price = ctrl.get('item_price')?.value;
    const qty = ctrl.get('quantity')?.value;
    const id = (ctrl.get('id')?.value || '').trim();
    const skuBlurred = !!ctrl.get('skuBlurred')?.value;

    const hasName = name.length > 0;
    const hasPrice = price !== '' && price !== null && !isNaN(Number(price));
    const qtyChanged = qty !== 1 && qty !== '1' && qty !== null && qty !== '';
    const hasId = id.length > 0;

    return hasName || hasPrice || qtyChanged || hasId || skuBlurred;
  }

  isRowSkuInvalid(ctrl: any): boolean {
    const id = (ctrl.get('id')?.value || '').trim();
    if (id.length > 0) return false;
    const touched = this.isRowTouched(ctrl);
    const skuBlurred = !!ctrl.get('skuBlurred')?.value;
    return (this.hasAttemptedSubmit || skuBlurred) && touched;
  }

  get lineItemsTotal(): number {
    const validRows = this.contents.controls.filter(
      (c) => (c.get('id')?.value || '').trim().length > 0
    );
    return validRows.reduce((sum, c) => {
      const qty = Number(c.get('quantity')?.value) || 1;
      const price = Number(c.get('item_price')?.value) || 0;
      return sum + qty * price;
    }, 0);
  }

  get hasValidLineItems(): boolean {
    return this.contents.controls.some(
      (c) => (c.get('id')?.value || '').trim().length > 0
    );
  }

  get isTotalMismatch(): boolean {
    if (!this.hasValidLineItems) return false;
    const currentVal = this.form?.get('value')?.value;
    if (currentVal === '' || currentVal === null || isNaN(Number(currentVal))) {
      return this.lineItemsTotal > 0;
    }
    return Math.abs(this.lineItemsTotal - Number(currentVal)) > 0.009;
  }

  applyLineItemsTotal() {
    this.form.patchValue({
      value: this.lineItemsTotal.toFixed(2),
    });
  }

  // --- Date Picker Helpers ---
  openDateTimePicker(inputEl: HTMLInputElement) {
    if (!inputEl) return;
    if (typeof (inputEl as any).showPicker === 'function') {
      try {
        (inputEl as any).showPicker();
        return;
      } catch (e) {
        // Fallback to focus
      }
    }
    inputEl.focus();
    inputEl.click();
  }

  // --- Modal Open & Close ---
  openModal(content: TemplateRef<any>) {
    this.submissionResult = null;
    this.dateError = '';
    this.isProductsCollapsed = true;
    this.selectedStorefrontOrder = null;
    this.showStorefrontOrderError = false;
    // Always default to 'test' mode with a clean form every time modal opens
    this.setMode('test', true);

    if (this.capiPixels && this.capiPixels.length > 0) {
      const cur = this.form.get('pixel_id')?.value;
      if (!cur || !this.capiPixels.some((p) => p.pixel_id === cur)) {
        this.form.patchValue({ pixel_id: this.capiPixels[0].pixel_id });
      }
    } else {
      this.form.patchValue({ pixel_id: '' });
    }

    const currentSource = this.form.get('action_source')?.value || 'website';
    this.calcDateBounds(currentSource);
    if (currentSource === 'website') {
      this.loadStorefrontOrders();
    }

    this.modalRef = this.modalService.open(content, {
      backdrop: 'static',
      centered: true,
      scrollable: true,
      size: 'lg',
      windowClass: 'modern-manual-pixel-modal',
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    if (this.confirmModalRef) {
      this.confirmModalRef.close();
      this.confirmModalRef = null;
    }
  }

  getEffectiveEventName(): string {
    const selected = this.form.get('event_select')?.value;
    if (selected === 'CUSTOM') {
      return (this.form.get('custom_event_name')?.value || '').trim();
    }
    return (selected || '').trim();
  }

  generateReferenceId(actionSource: string): string {
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    const now = new Date();
    const yyyymmddHHmmss = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let rand = '';
    for (let i = 0; i < 6; i++) {
      rand += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const source = actionSource || 'sale';
    return `manual_${source}_${yyyymmddHHmmss}_${rand}`;
  }

  scrollToTop() {
    setTimeout(() => {
      // 1. Scroll the modal body if it is the scrollable container
      const modalBody = document.querySelector('.modern-manual-pixel-modal .modal-body, .modern-modal-body') as HTMLElement;
      if (modalBody) {
        if (typeof modalBody.scrollTo === 'function') {
          modalBody.scrollTo({ top: 0, behavior: 'smooth' });
        }
        modalBody.scrollTop = 0;
      }

      // 2. Scroll the modal dialog or modal wrapper if it's the scrollable container
      const modalContainer = document.querySelector('.modern-manual-pixel-modal') as HTMLElement;
      if (modalContainer) {
        if (typeof modalContainer.scrollTo === 'function') {
          modalContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }
        modalContainer.scrollTop = 0;
      }

      // 3. Scroll top error banner or modal header into view
      const topTarget =
        document.getElementById('manualEventErrorBanner') ||
        document.querySelector('.modern-manual-pixel-modal .modern-alert-error') ||
        document.getElementById('manual-event-title') ||
        document.querySelector('.modern-manual-pixel-modal .modal-header');

      if (topTarget && typeof topTarget.scrollIntoView === 'function') {
        topTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  scrollToFirstError() {
    this.scrollToTop();
  }

  handlePrimaryAction() {
    this.submissionResult = null;
    this.hasAttemptedSubmit = true;

    // In Live mode with Storefront, an order MUST be selected
    if (this.form.get('action_source')?.value === 'website' && this.mode === 'live' && !this.selectedStorefrontOrder) {
      this.showStorefrontOrderError = true;
      this.submissionResult = {
        status: 'error',
        message: this.utils.translate('Select the order you want to send.'),
      };
      this.scrollToTop();
      return;
    }

    if (!this.validateEventDate()) {
      this.scrollToTop();
      return;
    }

    const pixelId = (this.form.get('pixel_id')?.value || '').trim();
    if (!pixelId) {
      this.submissionResult = {
        status: 'error',
        message: this.utils.translate('Please select a configured Facebook Pixel ID.'),
      };
      this.scrollToTop();
      return;
    }

    const eventName = this.getEffectiveEventName();
    if (!eventName) {
      this.submissionResult = {
        status: 'error',
        message: this.utils.translate('Please select or specify an Event Name.'),
      };
      this.scrollToTop();
      return;
    }

    // Check if any touched line item is missing SKU
    const invalidRowIndex = this.contents.controls.findIndex((ctrl) => {
      const id = (ctrl.get('id')?.value || '').trim();
      return this.isRowTouched(ctrl) && !id;
    });

    if (invalidRowIndex >= 0) {
      this.isProductsCollapsed = false;
      this.submissionResult = {
        status: 'error',
        message: this.utils.translate('Product SKU is required for all added line items.'),
      };
      setTimeout(() => {
        const el = document.getElementById(`skuInput_${invalidRowIndex}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }

    if (this.isTotalMismatch) {
      this.isProductsCollapsed = false;
    }

    if (this.hasInvalidEmail) {
      this.submissionResult = {
        status: 'error',
        message: this.utils.translate('Enter a valid email address, or leave it empty.'),
      };
      this.scrollToTop();
      return;
    }

    if (this.mode === 'live') {
      if (!this.form.get('action_source')?.value) {
        this.submissionResult = {
          status: 'error',
          message: this.utils.translate('Choose where this sale happened.'),
        };
        this.scrollToTop();
        return;
      }

      if (this.isConversionAmountRequired) {
        const amountVal = this.form.get('value')?.value;
        if (amountVal === '' || amountVal === null || isNaN(Number(amountVal)) || Number(amountVal) <= 0) {
          this.submissionResult = {
            status: 'error',
            message: this.utils.translate('Enter the sale amount.'),
          };
          this.scrollToTop();
          return;
        }
      }

      if (!this.hasCustomerIdentifier) {
        this.submissionResult = {
          status: 'error',
          message: this.utils.translate('Add an email or phone number so Meta can match this sale to your ads.'),
        };
        this.scrollToTop();
        return;
      }

      if (!this.form.get('customer_consent')?.value) {
        this.submissionResult = {
          status: 'error',
          message: this.utils.translate("Please confirm you have the customer's consent."),
        };
        this.scrollToTop();
        return;
      }

      // If order_id / reference number is empty in live mode, auto-generate on submit
      if (!this.form.get('order_id')?.value?.trim()) {
        const autoRef = this.generateReferenceId(this.form.get('action_source')?.value);
        this.autoGeneratedOrderId = autoRef;
        this.form.patchValue({ order_id: autoRef });
      }

      // Open Live confirmation modal
      this.confirmModalRef = this.modalService.open(this.liveConfirmModal, {
        centered: true,
        backdrop: 'static',
        size: 'md',
        windowClass: 'live-confirm-modal',
      });
    } else {
      // Test Mode
      const code = (this.form.get('test_event_code')?.value || '').trim();
      if (!code) {
        this.submissionResult = {
          status: 'error',
          message: this.utils.translate('Test Event Code is required in Test mode. Find it in Meta Events Manager > Test Events.'),
        };
        this.scrollToTop();
        return;
      }

      if (this.isConversionAmountRequired) {
        const amountVal = this.form.get('value')?.value;
        if (amountVal === '' || amountVal === null || isNaN(Number(amountVal)) || Number(amountVal) <= 0) {
          this.submissionResult = {
            status: 'error',
            message: this.utils.translate('Enter the sale amount. Meta requires a value and currency for Purchase events.'),
          };
          this.scrollToTop();
          return;
        }
      }

      if (!this.hasCustomerIdentifier) {
        this.submissionResult = {
          status: 'error',
          message: this.utils.translate('Add an email or phone number so Meta can match this event.'),
        };
        this.scrollToTop();
        return;
      }

      // If order_id / reference number is empty in test mode, auto-generate on submit
      if (!this.form.get('order_id')?.value?.trim()) {
        const autoRef = this.generateReferenceId(this.form.get('action_source')?.value || 'test');
        this.autoGeneratedOrderId = autoRef;
        this.form.patchValue({ order_id: autoRef });
      }

      this.executePushEvent();
    }
  }

  confirmAndPushLive() {
    if (this.confirmModalRef) {
      this.confirmModalRef.close();
      this.confirmModalRef = null;
    }
    this.executePushEvent();
  }

  executePushEvent() {
    const val = this.form.value;
    const pixelId = (val.pixel_id || '').trim();
    const eventName = this.getEffectiveEventName();
    const parsedDate = new Date(val.event_time || new Date());
    const eventTimeUnix = Math.floor(parsedDate.getTime() / 1000);

    // Build custom_data
    const customData: Record<string, any> = {};
    if (val.value !== null && val.value !== '' && !isNaN(Number(val.value))) {
      customData.value = Number(Number(val.value).toFixed(2));
      customData.currency = val.currency || 'MYR';
    }
    if (val.order_id) {
      customData.order_id = String(val.order_id).trim();
    }

    // Build products payload automatically from line item rows
    const validRows = this.contents.controls
      .map((ctrl) => ({
        id: (ctrl.get('id')?.value || '').trim(),
        name: (ctrl.get('name')?.value || '').trim(),
        quantity: Math.max(1, Math.floor(Number(ctrl.get('quantity')?.value) || 1)),
        item_price: ctrl.get('item_price')?.value,
      }))
      .filter((row) => row.id.length > 0);

    if (validRows.length > 0) {
      const lineItems: ContentLineItem[] = validRows.map((row) => {
        const item: ContentLineItem = {
          id: row.id,
          quantity: row.quantity,
        };
        if (row.item_price !== '' && row.item_price !== null && !isNaN(Number(row.item_price))) {
          item.item_price = Number(Number(row.item_price).toFixed(2));
        }
        return item;
      });

      customData.content_type = 'product';
      customData.contents = lineItems;
      customData.content_ids = validRows.map((r) => r.id);
      customData.num_items = validRows.reduce((sum, r) => sum + r.quantity, 0);
    }

    // Normalized user_data
    const userData: Record<string, any> = {};
    if (this.hasCustomerEmail) {
      userData.em = val.customer_email.trim().toLowerCase();
    }
    const phone = this.getCombinedPhone();
    if (phone && this.hasCustomerPhone) {
      userData.ph = phone;
    }
    if (val.first_name) {
      userData.fn = val.first_name.trim().toLowerCase();
    }
    if (val.last_name) {
      userData.ln = val.last_name.trim().toLowerCase();
    }
    if (val.city) {
      userData.ct = val.city.trim().toLowerCase();
    }
    if (val.postcode) {
      userData.zp = val.postcode.trim().toLowerCase();
    }
    if (val.country) {
      userData.country = val.country.trim().toLowerCase();
    }
    if (val.customer_id) {
      userData.external_id = val.customer_id.trim();
    }

    // Include unhashed browser & click data if prefilled from order (Requirement 5)
    if (this.selectedStorefrontOrder?.browser_data) {
      const bd = this.selectedStorefrontOrder.browser_data;
      if (bd.client_ip_address) userData.client_ip_address = bd.client_ip_address;
      if (bd.client_user_agent) userData.client_user_agent = bd.client_user_agent;
      if (bd.fbp) userData.fbp = bd.fbp;
      if (bd.fbc) userData.fbc = bd.fbc;
    }

    const eventPayload: Record<string, any> = {
      event_name: eventName,
      event_time: eventTimeUnix,
      action_source: val.action_source || 'physical_store',
      user_data: userData,
    };

    if (Object.keys(customData).length > 0) {
      eventPayload.custom_data = customData;
    }

    // event_id for deduplication
    if (val.order_id) {
      eventPayload.event_id = String(val.order_id).trim();
    }

    // event_source_url for storefront events
    if (val.action_source === 'website') {
      const sourceUrl = this.selectedStorefrontOrder?.event_source_url || window.location.origin;
      if (sourceUrl) {
        eventPayload.event_source_url = sourceUrl;
      }
    }

    const requestBody: Record<string, any> = {
      store_id: this.storeId || localStorage.getItem('store_id'),
      pixel_id: pixelId,
      events: [eventPayload],
    };

    if (this.mode === 'test' && val.test_event_code) {
      requestBody.test_event_code = val.test_event_code.trim();
    }

    this.isSubmitting = true;
    const token = this.bearer || localStorage.getItem('bearer') || '';

    this.storeService.triggerPixelEvent(token, requestBody).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        const traceId = res?.fbtrace_id || res?.data?.fbtrace_id || (Array.isArray(res?.data) && res?.data[0]?.fbtrace_id) || '';

        this.submissionResult = {
          status: 'success',
          message: this.utils.translate(
            `Event "${eventName}" successfully delivered to Meta Pixel ${pixelId} in ${this.mode.toUpperCase()} mode!`
          ),
          traceId,
          timestamp: new Date().toLocaleTimeString(),
          mode: this.mode,
        };

        // Track Mixpanel Success Event
        this.trackMixpanelEvent('Pixel Manual Push Success', val, res);

        // Clear the auto-generated reference so the next push gets a fresh event_id
        // (otherwise Meta deduplicates it). User-entered references are kept.
        if (this.autoGeneratedOrderId && this.form.get('order_id')?.value === this.autoGeneratedOrderId) {
          this.form.patchValue({ order_id: '' });
        }
        this.autoGeneratedOrderId = '';

        this.utils.notify.showSuccess(
          this.utils.translate(`Event "${eventName}" pushed successfully (${this.mode.toUpperCase()})!`),
          'Meta Pixel'
        );

        this.scrollToTop();

        // Refresh orders list to reflect sent status
        if (val.action_source === 'website') {
          this.loadStorefrontOrders();
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        let msg =
          (typeof err?.error?.message === 'string' ? err.error.message : '') ||
          (Array.isArray(err?.error?.message) && err.error.message[0]?.error ? err.error.message[0].error : '') ||
          (Array.isArray(err?.error?.data) && err.error.data[0]?.error ? err.error.data[0].error : '') ||
          (typeof err?.error?.data === 'string' ? err.error.data : '') ||
          err?.error?.error ||
          err?.message ||
          this.utils.translate('Failed to send event. Please check Pixel ID or connection and try again.');

        if (typeof msg !== 'string') {
          try {
            msg = JSON.stringify(msg);
          } catch (e) {
            msg = this.utils.translate('Failed to send event to Meta.');
          }
        }

        this.submissionResult = {
          status: 'error',
          message: msg,
          timestamp: new Date().toLocaleTimeString(),
        };

        // Track Mixpanel Fail Event
        this.trackMixpanelEvent('Pixel Manual Push Fail', val, err?.error || err?.message || msg);

        this.utils.notify.showError(msg, 'Meta Pixel Error');
        this.scrollToTop();
      },
    });
  }

  private trackMixpanelEvent(
    eventType: 'Pixel Manual Push Success' | 'Pixel Manual Push Fail',
    val: any,
    responseData: any
  ) {
    try {
      const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
      const dealerId = info.d_id || info.dealer_id || '';
      const dealerUsername = info.dealer_name || info.username || '';

      const rawContents = this.contents.value || [];
      const validContents = rawContents
        .filter((c: any) => (c?.id || '').trim().length > 0)
        .map((c: any) => ({
          id: c.id.trim(),
          name: c.name?.trim() || undefined,
          quantity: Number(c.quantity) || 1,
          item_price: c.item_price !== '' && c.item_price !== null && !isNaN(Number(c.item_price))
            ? Number(c.item_price)
            : undefined,
        }));

      const productsTotalQty = validContents.reduce(
        (sum: number, c: any) => sum + (c.quantity || 1),
        0
      );

      const dataEvent = {
        data: {
          d_id: dealerId,
          distinct_id: dealerId,
          dealer_username: dealerUsername,
          type: eventType,
          mode: this.mode, // 'test' | 'live'
          pixel_id: val.pixel_id,
          store_id: this.storeId || localStorage.getItem('store_id') || '',
          platform: 'dashboard',

          // Where did this sale happen?
          action_source: val.action_source,
          action_source_title: this.currentActionSourceObj?.title || val.action_source,

          // Conversion & Value
          event_name: this.getEffectiveEventName(),
          currency: val.currency || 'MYR',
          amount: val.value !== '' && val.value !== null && !isNaN(Number(val.value)) ? Number(val.value) : undefined,
          order_id: val.order_id || undefined,
          store_order_id: this.selectedStorefrontOrder?.store_order_id || undefined,
          fiv5s_order_id: this.selectedStorefrontOrder?.fiv5s_order_id || undefined,
          event_time: val.event_time,

          // Customer Details
          match_strength: this.matchStrength,
          has_email: !!val.customer_email?.trim(),
          has_phone: !!(val.customer_phone_number?.trim()),
          customer_country: val.country || undefined,
          customer_city: val.city || undefined,
          customer_postcode: val.postcode || undefined,
          has_customer_id: !!val.customer_id?.trim(),
          customer_consent: !!val.customer_consent,

          // Products
          products_count: validContents.length,
          products_total_qty: productsTotalQty,
          products: validContents.length > 0 ? validContents : undefined,

          // Response / Error
          response: responseData,
          trace_id:
            responseData?.fbtrace_id ||
            responseData?.data?.fbtrace_id ||
            (Array.isArray(responseData?.data) && responseData?.data[0]?.fbtrace_id) ||
            undefined,
        },
        application: 'dashboard',
        driver: 'mixpanel',
      };

      this.storeService.publish_event(dataEvent).subscribe({
        next: (res: any) => {
          // Logged silently
        },
        error: (err: any) => {
          console.warn('Mixpanel tracking error:', err);
        },
      });
    } catch (e) {
      console.warn('Failed to publish Mixpanel event:', e);
    }
  }

  dismissAlert() {
    this.submissionResult = null;
  }

  resetForm() {
    this.submissionResult = null;
    this.dateError = '';
    this.initForm();
    this.setMode('test');
  }
}
